import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeCompanyBrandingPayload, resolveCompanyBrandingName } from './wpaCompanyBranding';
import { getCartDocsCount, clearSessionScope } from './wpaCartStore';
import WpaPwaManifestAndInstall from './WpaPwaManifestAndInstall';
import { useWpaCustomer } from './WpaCustomerContext';
import { resetMobileZoom } from './wpaViewportZoom';

const PRIMARY = '#4CAF50';
/** ?��?欄�?度�??��?首�? banner `position: sticky` ??`top` ?�對�?此�?*/
export const WPA_LAYOUT_HEADER_HEIGHT = 56;

/**
 * ??login 後選??UI 語�???customer 顯示?�稱?? * 後端 /api/wpa-auth/me ?��? cust ?�件??nameEn / nameCn / nameSc / displayName
 * （由 CommerceLocaleHelper.FormatCustomerDisplayName ?��?，�???Name1/Name2/Name3 ?��???locale）�? * ?��??�目標�?系就 fallback ??displayName ???��?語系 ??customerNo?? */
function pickCustomerName(cust, lang) {
  if (!cust) return '';
  const en = (cust.nameEn || '').trim();
  const tc = (cust.nameCn || '').trim();
  const sc = (cust.nameSc || '').trim();
  const def = (cust.displayName || '').trim();
  let primary;
  if (lang === 'en') primary = en || def || tc || sc;
  else if (lang === 'zh-SC') primary = sc || def || tc || en;
  else primary = tc || def || en || sc; // zh-TC ?�設
  return primary || cust.customerNo || '-';
}

/**
 * ??login 後選??UI 語�???customer ?�貨?��??? * 後端?��? cust.deliveryAddressEn / cust.deliveryAddressCn 已是?��? 1 \n �?2 \n �?3?��?併�?串�? * en ???��??��??��?，fallback 中�?；其他�?zh-TC / zh-SC）�? ?��?中�??��?，fallback ?��??? * （�?端目?�只?�?�中／英?��??��?；簡體沿?�中?�。�?
 */
function pickCustomerAddress(cust, lang) {
  if (!cust) return '';
  const en = (cust.deliveryAddressEn || '').trim();
  const cn = (cust.deliveryAddressCn || '').trim();
  if (lang === 'en') return en || cn;
  return cn || en;
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 480,
    margin: '0 auto',
    position: 'relative',
  },
  header: {
    position: 'sticky',
    top: 0,
    width: '100%',
    maxWidth: 480,
    height: WPA_LAYOUT_HEADER_HEIGHT,
    background: PRIMARY,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
    zIndex: 1000,
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  },
  headerBtn: {
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: 34,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    minWidth: 48,
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  content: {
    flex: 1,
    background: '#fff',
    minHeight: `calc(100vh - ${WPA_LAYOUT_HEADER_HEIGHT}px)`,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 1100,
  },
  drawer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 260,
    height: '100%',
    background: '#fff',
    zIndex: 1200,
    boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.25s ease',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 20px',
    fontSize: 15,
    color: '#333',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    borderBottom: '1px solid #f0f0f0',
  },
  menuItemActive: {
    color: PRIMARY,
    fontWeight: 600,
    background: '#f0faf0',
  },
  menuIcon: {
    fontSize: 20,
    width: 24,
    textAlign: 'center',
  },
  cartBadge: {
    position: 'absolute',
    top: 2,
    right: 2,
    background: '#ff5252',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 999,
    minWidth: 22,
    height: 22,
    padding: '0 5px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIconSvg: {
    display: 'block',
    width: 32,
    height: 32,
    color: '#fff',
    flexShrink: 0,
  },
};

function getMenuItems(companyId, t) {
  const base = `/${companyId}/wpa`;
  return [
    { key: 'home', label: t('wpa.layout.menuHome'), icon: '🏠', path: `${base}/home` },
    { key: 'categories', label: t('wpa.layout.menuCategories'), icon: '📁', path: `${base}/categories` },
    { key: 'cart', label: t('wpa.layout.menuCart'), icon: '🛒', path: `${base}/cart` },
    { key: 'orders', label: t('wpa.layout.menuOrders'), icon: '📋', path: `${base}/orders` },
    { key: 'account', label: t('wpa.layout.menuAccount'), icon: '👤', path: `${base}/account` },
  ];
}

function getCartCount() {
  return getCartDocsCount();
}

export default function WpaLayout({
  title = 'PWA',
  children,
  /** 'menu'：漢?�選?��?'back'：�??��??�?��? onBack�?*/
  leadingMode = 'menu',
  onBack,
}) {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId } = useParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartCount, setCartCount] = useState(getCartCount);
  const [companyBranding, setCompanyBranding] = useState(() => ({ name: '', nameLocales: null }));
  const documentTitleBeforeWpaRef = useRef(null);
  const {
    currentCustomer,
    availableCustomers,
    customerPickerOpen,
    setCustomerPickerOpen,
    setCurrentCustomer,
  } = useWpaCustomer();
  const menuItems = getMenuItems(companyId, t);
  const base = `/${companyId}/wpa`;

  const checkSession = useCallback(() => {
    const token = localStorage.getItem('wpa_token');
    if (!token) {
      navigate(`${base}/login`, { replace: true });
    }
  }, [navigate, base]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const res = await fetch(`/api/wpa-auth/company-branding/${companyId}`);
        if (!res.ok) return;
        const data = await res.json();
        setCompanyBranding(normalizeCompanyBrandingPayload(data));
        if (data.timezone) {
          localStorage.setItem('wpa_company_timezone', data.timezone);
        }
      } catch {
        // ignore
      }
    })();
  }, [companyId]);

  const companyDisplayName = useMemo(
    () => resolveCompanyBrandingName(companyBranding, currentLanguage),
    [companyBranding, currentLanguage],
  );

  useEffect(() => {
    if (documentTitleBeforeWpaRef.current === null) {
      documentTitleBeforeWpaRef.current = document.title;
    }
    if (companyDisplayName) {
      document.title = companyDisplayName;
    } else {
      document.title = t('wpaLogin.brandSubtitle');
    }
  }, [companyDisplayName, t]);

  useEffect(() => {
    return () => {
      const previousTitle = documentTitleBeforeWpaRef.current;
      if (previousTitle != null) document.title = previousTitle;
    };
  }, []);

  // 每次進入 WPA 頁面（含切 customer 後 reload）都把 iOS 殘留的 auto-zoom 還原到 1.0。
  useEffect(() => {
    resetMobileZoom();
  }, []);

  useEffect(() => {
    const onStorage = () => setCartCount(getCartCount());
    window.addEventListener('storage', onStorage);
    const interval = setInterval(() => setCartCount(getCartCount()), 1000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const selectCustomer = (cust) => {
    localStorage.setItem('wpa_current_customer', JSON.stringify(cust));
    setCurrentCustomer(cust);
    setCustomerPickerOpen(false);
    // 先還原 iOS 可能殘留的縮放，再 reload（reload 後 WpaLayout mount 亦會再做一次保險）
    resetMobileZoom();
    window.location.reload();
  };

  const handleLogout = () => {
    localStorage.removeItem('wpa_token');
    localStorage.removeItem('wpa_current_customer');
    localStorage.removeItem('wpa_available_customers');
    localStorage.removeItem('wpa_company_timezone');
    clearSessionScope();
    // 購物車依 scope 存在 wpa_cart_docs__{company}__{identity}，登出不刪除，下次同帳登入可繼續使用
    navigate(`${base}/login`, { replace: true });
  };

  return (
    <div style={styles.container}>
      <WpaPwaManifestAndInstall companyId={companyId} />
      <header style={styles.header}>
        {leadingMode === 'back' ? (
          <button
            type="button"
            style={styles.headerBtn}
            onClick={() => (onBack ? onBack() : navigate(-1))}
            aria-label={t('wpa.layout.ariaBack')}
          >
            <span style={{ fontSize: 34, lineHeight: 1, fontWeight: 600, display: 'block' }}>{'\u2190'}</span>
          </button>
        ) : (
          <button type="button" style={styles.headerBtn} onClick={() => setDrawerOpen(true)} aria-label={t('wpa.layout.ariaMenu')}>
            <span style={{ fontSize: 28, lineHeight: 1, display: 'block' }}>{'\u2630'}</span>
          </button>
        )}
        <span style={styles.headerTitle}>{title}</span>
        <div style={styles.headerRight}>
          <button
            type="button"
            style={styles.headerBtn}
            onClick={() => navigate(`${base}/products?search=`)}
            aria-label={t('wpa.layout.ariaSearch')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={styles.headerIconSvg}
              aria-hidden="true"
            >
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
          </button>
          <button
            type="button"
            style={{ ...styles.headerBtn, position: 'relative' }}
            onClick={() => navigate(`${base}/cart`)}
            aria-label={t('wpa.layout.ariaCart')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={styles.headerIconSvg}
              aria-hidden="true"
            >
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0019 4H6.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            {cartCount > 0 && <span style={styles.cartBadge}>{cartCount > 99 ? '99+' : cartCount}</span>}
          </button>
        </div>
      </header>

      {drawerOpen && (
        <>
          <div style={styles.overlay} onClick={() => setDrawerOpen(false)} role="presentation" />
          <nav style={styles.drawer}>
            {currentCustomer && (
              <button
                type="button"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  width: '100%', padding: '12px 20px', background: '#f0f7f0',
                  border: 'none', borderBottom: '1px solid #e0e0e0', cursor: 'pointer',
                  textAlign: 'left', fontSize: 16, color: '#333', lineHeight: 1.4,
                }}
                onClick={() => { if (availableCustomers.length > 1) setCustomerPickerOpen(true); }}
              >
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  <span style={{ fontWeight: 600 }}>{pickCustomerName(currentCustomer, currentLanguage)}</span>
                  <span style={{ color: '#888', marginLeft: 6 }}>{currentCustomer.customerNo}-{currentCustomer.branchNo}</span>
                </span>
                {availableCustomers.length > 1 && <span style={{ fontSize: 16 }}>🔄</span>}
              </button>
            )}
            {menuItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <button
                  type="button"
                  key={item.key}
                  style={{ ...styles.menuItem, ...(active ? styles.menuItemActive : {}) }}
                  onClick={() => {
                    navigate(item.path);
                    setDrawerOpen(false);
                  }}
                >
                  <span style={styles.menuIcon}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
            <button
              type="button"
              style={{ ...styles.menuItem, marginTop: 'auto' }}
              onClick={() => {
                navigate(`${base}/change-password`);
                setDrawerOpen(false);
              }}
            >
              <span style={styles.menuIcon}>🔑</span>
              {t('wpa.layout.menuChangePassword')}
            </button>
            <button
              type="button"
              style={{ ...styles.menuItem, color: '#e53935' }}
              onClick={handleLogout}
            >
              <span style={styles.menuIcon}>🚪</span>
              {t('wpa.layout.logout')}
            </button>
          </nav>
        </>
      )}

      <main style={styles.content}>{children}</main>

      {customerPickerOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000 }} onClick={() => setCustomerPickerOpen(false)} role="presentation" />
          <div style={{
            position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
            width: '100%', maxWidth: 480, background: '#fff', borderRadius: '16px 16px 0 0',
            zIndex: 2001, maxHeight: '60vh', overflow: 'auto', boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', fontWeight: 600, fontSize: 16 }}>
              {t('wpa.layout.selectCustomer')}
            </div>
            {availableCustomers.map((cust) => {
              const isActive = currentCustomer?.id === cust.id;
              return (
                <button
                  type="button"
                  key={cust.id}
                  style={{
                    display: 'block', width: '100%', padding: '14px 20px', border: 'none',
                    borderBottom: '1px solid #f5f5f5', cursor: 'pointer', textAlign: 'left',
                    background: isActive ? '#e8f5e9' : '#fff', fontSize: 14,
                  }}
                  onClick={() => selectCustomer(cust)}
                >
                  <div style={{ fontWeight: 600, color: '#333' }}>{pickCustomerName(cust, currentLanguage)}</div>
                  {(() => {
                    const addr = pickCustomerAddress(cust, currentLanguage);
                    return (
                      <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>
                        {cust.customerNo}-{cust.branchNo}
                        {addr && ` — ${addr}`}
                      </div>
                    );
                  })()}
                  {isActive && <span style={{ color: PRIMARY, fontSize: 12, fontWeight: 600 }}>✓ {t('wpa.layout.current')}</span>}
                </button>
              );
            })}
            <div style={{ padding: 12, textAlign: 'center' }}>
              <button
                type="button"
                style={{ padding: '8px 24px', border: '1px solid #ddd', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}
                onClick={() => setCustomerPickerOpen(false)}
              >
                {t('wpa.layout.cancel')}
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
